import { useEffect } from "react";
import CompanyService from "../../../../services/CompanyService";
import AnnouncementModal from "./AnnouncementModal";
import {
  selectCurrentAnnouncement,
  useAnnouncementsStore
} from "./announcementsStore";

const SESSION_KEY_PREFIX = "announcements_popup_shown_";

/**
 * "Al iniciar sesión" = primera carga del layout autenticado en esta sesión del navegador:
 * recargar la página no vuelve a abrir el popup; una sesión nueva sí (GDC-5185).
 */
const wasShownThisSession = (userId: string) => {
  try {
    return sessionStorage.getItem(SESSION_KEY_PREFIX + userId) === "1";
  } catch {
    return false;
  }
};

const markShownThisSession = (userId: string) => {
  try {
    sessionStorage.setItem(SESSION_KEY_PREFIX + userId, "1");
  } catch {
    // Sin sessionStorage el popup se abre en cada carga.
  }
};

/** Carga los comunicados y abre el primero pendiente al iniciar sesión. */
const AnnouncementsPopup = () => {
  const userId = CompanyService.getUserid();
  const status = useAnnouncementsStore((state) => state.status);
  const popup = useAnnouncementsStore((state) => state.popup);
  const current = useAnnouncementsStore(selectCurrentAnnouncement);
  const dismissedIds = useAnnouncementsStore((state) => state.dismissedIds);
  const {
    load,
    openLoginPopup,
    closePopup,
    goTo,
    dismiss,
    trackView,
    trackClick
  } = useAnnouncementsStore.getState();

  useEffect(() => {
    if (userId) {
      load(userId);
    }
  }, [load, userId]);

  useEffect(() => {
    if (status !== "loaded" || !userId || wasShownThisSession(userId)) {
      return;
    }
    markShownThisSession(userId);
    openLoginPopup();
  }, [openLoginPopup, status, userId]);

  // La vista se reporta solo desde el popup del login; reabrir desde la campanita no cuenta.
  const currentId = current?.announcementId;
  useEffect(() => {
    if (popup.open && popup.source === "login" && currentId !== undefined) {
      trackView(currentId);
    }
  }, [currentId, popup.open, popup.source, trackView]);

  const total = popup.queue.length;
  useEffect(() => {
    if (!popup.open || total <= 1) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        goTo(popup.index - 1);
      } else if (event.key === "ArrowRight") {
        goTo(popup.index + 1);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [goTo, popup.index, popup.open, total]);

  if (!current) {
    return null;
  }

  const isLoginPopup = popup.source === "login";

  return (
    <AnnouncementModal
      announcement={current}
      open={popup.open}
      onClose={closePopup}
      position={isLoginPopup ? { index: popup.index, total } : undefined}
      onPrev={() => goTo(popup.index - 1)}
      onNext={() => goTo(popup.index + 1)}
      showDontShowAgain
      dontShowAgain={dismissedIds.includes(current.announcementId)}
      onDontShowAgainChange={(checked) => {
        if (checked) {
          dismiss(current.announcementId);
        }
      }}
      onCtaClick={() => trackClick(current.announcementId)}
    />
  );
};

export default AnnouncementsPopup;
